import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { validate as emailValidate } from 'email-validator';
import { Card, TextInput, Text, Button, H4 } from '@revdotcom/vaporwave';

import { useSubmit } from '../useSubmit';
import { fixupEvents } from '../helpers';
import { CardLogo } from '../components/CardLogo';
import { UnderLinks } from '../components/UnderLinks';
import styles from '../../styles';
import { type ForgotPasswordProps } from '../../codegen/ForgotPasswordProps';
import { type ForgotPasswordSubmit } from '../../codegen/ForgotPasswordSubmit';
import { type FlowProps } from '../../FlowProps';

/**
 * Forgot Password page.
 * @param {ForgotPasswordProps} props page parameters
 * @returns {object} component
 */
const ForgotPasswordPage = (props: FlowProps<ForgotPasswordProps>) => {
    const [searchParams] = useSearchParams();

    const returnUrl = searchParams.get('returnUrl') ?? undefined;
    const email = searchParams.get('email') ?? undefined;

    // enable react-hook-form form orchestration
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<ForgotPasswordSubmit>({
        mode: 'onChange',
        criteriaMode: 'all',
        defaultValues: { email },
    });

    // set up our ajax submit handler
    const { onSubmit, submitError, isRedirecting } = useSubmit('forgot', {
        captchaKey: props.captchaKey,
        antiforgeryToken: props.verificationToken,
        preSubmit: useCallback(
            (data: ForgotPasswordSubmit) => {
                data.returnUrl = returnUrl;
            },
            [returnUrl],
        ),
    });

    if (props.emailSent) return <EmailSent {...props} />;

    return (
        <Card sx={styles.cardForgotPassword}>
            <CardLogo {...props} />

            <div sx={styles.cardContainer}>
                <H4 sx={styles.cardTitle}>Forgot Password</H4>

                <Text sx={styles.cardText}>
                    Enter your email address to reset your password.
                </Text>

                <form sx={styles.cardForm} onSubmit={handleSubmit(onSubmit)}>
                    <div sx={{ position: 'relative' }}>
                        <TextInput
                            label="Email address"
                            autoComplete="userName"
                            inputMode="email"
                            size="large"
                            /*isRequired*/
                            validationState={errors.email && 'invalid'}
                            helperText={
                                errors.email &&
                                'Please enter a valid email address.'
                            }
                            sx={{ variant: 'forms.identity.login.userName' }}
                            {...fixupEvents(
                                register('email', {
                                    required: true,
                                    validate: emailValidate,
                                }),
                            )}
                        />
                        {submitError && (
                            <Text sx={styles.cardError}>{submitError}</Text>
                        )}
                    </div>

                    <Button
                        sx={styles.cardResetPasswordButton}
                        type="submit"
                        sizeVariant="small"
                        isDisabled={isSubmitting || isRedirecting}
                    >
                        Reset password
                    </Button>
                </form>
            </div>

            <UnderLinks {...props} />
        </Card>
    );
};

/**
 * Renders the page in the state when e-mail is sent (intentionally not reloadable)
 * @param {ForgotPasswordProps }props page props
 * @returns {Element} JSX
 */
const EmailSent = (props: FlowProps<ForgotPasswordProps>) => {
    const [searchParams] = useSearchParams();

    const returnUrl = searchParams.get('returnUrl') ?? undefined;
    // as well provided: const email = searchParams.get('email') ?? undefined;

    const onContinue = useCallback(() => {
        const loginUrl =
            props.authorityEntryPoint && !returnUrl
                ? props.authorityEntryPoint
                : `/login?returnUrl=${encodeURIComponent(returnUrl ?? 'missing')}`;

        // force redirect (not SPA) to login page to ensure the state is rebuilt
        window.location.href = loginUrl;
    }, [returnUrl, props.authorityEntryPoint]);

    return (
        <Card sx={styles.cardForgotPassword}>
            <div sx={styles.cardLogo}>
                <CardLogo {...props} />
            </div>

            <div sx={styles.cardContainer}>
                <H4 sx={styles.cardTitle}>Email sent</H4>

                <Text sx={styles.cardText}>
                    If there is an account associated with this email address,
                    you will get an email with instructions on how to reset your
                    password.
                </Text>

                <Button
                    sx={styles.cardResetPasswordButton}
                    sizeVariant="small"
                    onPress={onContinue}
                >
                    Back to Sign in
                </Button>
            </div>

            <UnderLinks {...props} />
        </Card>
    );
};

export default ForgotPasswordPage;
